import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useRouter } from "next/router"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { i18n, useTranslation } from "next-i18next"
import Head from "next/head"
import BreadCrumb from "@/components/Default/BreadCrumb"
import AemGrid from "@/components/AemGrid"
import { CORE_GLOBAL_CONFIG_URL, footerUrl, headerUrl, previewContentPrefix } from "@/constants"
import {
  aemApimAxiosInstance,
  aemAxiosInstance,
  aemPreviewAxiosInstance,
} from "@/constants/api"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import {
  getClienIP,
  getScriptsAndStyles,
  getAnalyticsScript,
  getAccessToken,
  filterBackEndJson,
  removeAndCleanLink,
  performRedirect,
  buildUrlForAemApim,
} from "@/utils/helper"
import { getCartCount, getAriaLabel } from "@/utils/cart"
import {
  selectGenericState,
  setBrandLogoImg,
  closeMenu,
  openMenu,
  setActiveMainCategory,
  setRegionNav,
  setGlobalScripts,
  setGlobalStyles,
  setAuthNav,
  setDatalayerPageName,
  setGbhDataLayerCustom,
} from "@/store/features/genericSlice"
import usePageAuth from "@/hooks/usePageAuth"
import { selectCartState } from "@/store/features/cartSlice"
import { getDataLayerObj } from "@/utils/helper"
import ProductList from "@/components/ProductList/v2/ProductList"
import {
  getListFromClient,
  getListFromServer,
  getPlpParams,
  formatListingData,
} from "@/utils/product"
import { setPlpData } from "@/store/features/productListSlice"
import useIsSsr from "@/hooks/useIsSsr"
import PLPLoader from "@/components/ProductList/v2/PLPLoader"
import styles from "@/components/ProductList/v2/ProductListView/index.module.scss"
import productTileStyles from "@/components/ProductList/v2/ProductTile/index.module.scss"
import UpSellInstallServices from "@/components/Shared/UpsellInstallServices"
import ProductAccordion from "@/components/ProductDetail/v3/ProductAccordion"
import _isEmpty from "lodash/isEmpty"
import mappingData from "@/components/Shared/data.json"

/**
 * renders home page
 * @return {JSX.Element}
 * @param {object} props The props.
 */
const Listing = props => {
  const {
    tmpData: data,
    headertmp: header,
    footertmp: footer,
    ip,
    adobeAnalytic,
    globalScript,
    seoPageScript,
    footerScript,
    analyticsData,
    datalayerPageName,
    plpData,
    gbhDataLayerCustom,
    mappingData
  } = props
  const { t } = useTranslation("common")
  const dispatch = useDispatch()
  const router = useRouter()
  const isSsr = useIsSsr()
  const { cartUrl } = useSelector(selectCartState)
  const [pageRedirect, setPageRedirect] = useState("")
  const { redirectTarget: { page: { path = "" } = {} } = {} } = data || {}
  const {
    brandLogoImg,
    menu: { isOpen, mainCategoryActive, isRegionOpen, isAuthNavOpen },
  } = useSelector(selectGenericState)
  useEffect(() => {
    if (ip) sessionStorage.setItem("true-ip", ip)
  }, [ip])
  usePageAuth()
  const cartCount = getCartCount()
  const ariaLabel = getAriaLabel(t, cartCount)
  useEffect(() => {
    getScriptsAndStyles(footer)
  }, [footer])
  useEffect(() => {
    getAnalyticsScript(adobeAnalytic)
  }, [adobeAnalytic])
  useEffect(() => {
    getDataLayerObj(analyticsData)
  }, [analyticsData])
  useEffect(() => {
    if (path) {
      setPageRedirect(removeAndCleanLink(path))
    }
  }, [path])
  useEffect(() => {
    if (!isSsr && pageRedirect) {
      router.push(pageRedirect)
    }
  }, [isSsr, pageRedirect])
  useEffect(() => {
    // if (isSsr) return
    dispatch(setDatalayerPageName(datalayerPageName))
    dispatch(setGbhDataLayerCustom(gbhDataLayerCustom))
    if (plpData) {
      dispatch(
        setPlpData({ ...plpData, status: "succeeded", initialLoad: true })
      )
    }
    return () => {
      dispatch(setDatalayerPageName(""))
      dispatch(setGbhDataLayerCustom(""))
    }
  }, [datalayerPageName, gbhDataLayerCustom, plpData])
  const toggleMenu = () => {
    if (isOpen) {
      dispatch(closeMenu())
      dispatch(setAuthNav(false))
    } else {
      dispatch(openMenu())
    }
  }
  const redirect = () => {
    router.push(`${cartUrl}`)
  }
  const eventHandler = {
    store: {
      brandLogoImg: brandLogoImg,
      isOpen,
      mainCategoryActive,
      isRegionOpen,
      isAuthNavOpen,
      cartCount,
      ariaLabel,
      cartUrl,
      plp: { ...plpData, status: "succeeded" },
    },
    callbacks: {
      setBrandLogoImg: path => {
        dispatch(setBrandLogoImg(path))
      },
      closeMenu: () => {
        dispatch(closeMenu())
      },
      setMainCategory: payload => {
        dispatch(setActiveMainCategory(payload))
      },
      toggleMenu: () => {
        toggleMenu()
      },
      setRegionNav: active => {
        dispatch(setRegionNav(active))
      },
      setAuthNav: active => {
        dispatch(setAuthNav(active))
      },
      setGlobalScripts: item => {
        dispatch(setGlobalScripts(item))
      },
      setGlobalStyles: item => {
        dispatch(setGlobalStyles(item))
      },
      onClick: () => {
        redirect()
      },
    },
  }
  return (
    <>
      <Head>
        {data?.title && (
          <>
            <title>{data?.title}</title>
            <meta property="og:title" content={data?.title} />
          </>
        )}
        {data?.description && (
          <>
            <meta name="description" content={data?.description} />
            <meta property="og:description" content={data?.description} />
          </>
        )}
        <meta property="og:url" content="/en" />

        {data?.templateName && (
          <meta property="og:type" content={data?.templateName} />
        )}
      </Head>
      <>
        <GlobalPageHeader
          header={header}
          title={data?.title}
          globalScripting={globalScript}
          seoPageScripting={seoPageScript}
          mappingData={mappingData}
        />
        <style jsx global>
          {`
            @media print {
              @page {
                size: A4 portrait !important;
              }
            }
          `}
        </style>
        <div id={data.id} className={data.cssClassNames}>
          <AemGrid
            data={data}
            count={0}
            eventHandler={eventHandler}
            breadCrumb={(data, fullData) => (
              <BreadCrumb data={data} fullData={fullData} />
            )}
            productList={(data, eventHandler) => (
              <ProductList data={data} eventHandler={eventHandler} />
            )}
            productListFiller={() => (
              <div
                className={`${styles.productListWrapper} ${productTileStyles.productTileWrapper}`}
              >
                <PLPLoader />
              </div>
            )}
            upSellContainer={data => <UpSellInstallServices authData={data} />}
            productAccordion={(data, fullData) => (
              <ProductAccordion data={data} fullData={fullData} />
            )}
          />
        </div>
        <GlobalPageFooter footer={footer} footerScripting={footerScript} />
      </>
    </>
  )
}
/**
 *
 * @param {*} context
 * @return {Object}
 */
export async function getServerSideProps({ req, locale }) {
  if (!req.url) {
    console.log("No URL found in request", req.url)
    return {
      notFound: true,
    }
  }
  await i18n?.reloadResources()
  // let mappingData={}
  // const { url = "" } = req
  // const urlPathWithoutParams = url?.split("?")[0] ?? `/${locale}`
  // try{
  // const { data: configData = {} } = await aemAxiosInstance({
  //     url: `${CORE_GLOBAL_CONFIG_URL}?caConfig=true&path=/${locale}${urlPathWithoutParams}`,
  //     method: "GET",
  // })
  // if (!_isEmpty(configData)) {
  //     const {
  //       general: { productRangePath="" } = {},
  //     } = configData;
  //     ({ data: mappingData } = await aemAxiosInstance({
  //       url: `${CORE_GLOBAL_CONFIG_URL}?path=${productRangePath}`,
  //       method: "GET",
  //     }));
  //   }
  // }
  // catch(err){console.log(err, "error1")}
  const isPreviewMode = req.cookies["aem-author-preview-mode"]

  let plpData = {}
  const ip = getClienIP(req)

  const urlPath = req.url.match(/^([^?#&]+)/)[1]

  let data
  try {
    const response = isPreviewMode
      ? await aemPreviewAxiosInstance({
          url: `${previewContentPrefix}/${locale}${urlPath}.model.json`,
          method: "GET",
        })
      : await aemApimAxiosInstance({
          url: buildUrlForAemApim(`${urlPath}.model.json`),
          method: "GET",
        })
    data = response?.data
  } catch (err) {
    console.log("Failed fetching PLP Model JSON data from AEM", err)
    return {
      notFound: true,
    }
  }
  const globalScript = data?.globalScript
  const footerScript = data?.footerScript
  const topHeadScript = data?.topHeadScript ? data?.topHeadScript : null
  const seoPageScript = data?.seoPageScript ? data?.seoPageScript : null
  const gbhDataLayerCustom = {}
  if (data?.datalayerPageName) {
    gbhDataLayerCustom.datalayerPageName = data?.datalayerPageName
  }
  if (data?.eventPageType) {
    gbhDataLayerCustom.eventPageType = data?.eventPageType
  }
  if (data?.pageType) {
    gbhDataLayerCustom.pageType = data?.pageType
  }
  if (
    !data?.pageComponentProperties?.description &&
    data?.pageComponentProperties?.dataLayer &&
    data?.pageComponentProperties?.dataLayer[data?.pageComponentProperties?.id]
  ) {
    gbhDataLayerCustom.dataLayer =
      data?.pageComponentProperties?.dataLayer[
        data?.pageComponentProperties?.id
      ]
  }
  const categoryKey = getPlpParams(data, "categoryKey")

  const { data: header } = await aemApimAxiosInstance({
    url: data.headerExpFrg
      ? buildUrlForAemApim(`${data.headerExpFrg}.model.json`)
      : buildUrlForAemApim(`${headerUrl}.model.json`),
    method: "get",
  })

  const { data: footer } = await aemApimAxiosInstance({
    url: data.footerExpFrg
      ? buildUrlForAemApim(`${data.footerExpFrg}.model.json`)
      : buildUrlForAemApim(`${footerUrl}.model.json`),
    method: "GET",
  })

  const trimmedPath = urlPath.replace(/^\/+|\/+$/g, "")

  if (trimmedPath.split("/").length > 2 && categoryKey) {
    try {
      const data = await getListFromServer(categoryKey, "GST")
      plpData = await formatListingData(data, true)
    } catch (error) {
      plpData = null
      console.log("Error in getting PLP data", error)
    }
  }

  const analyticsData = data?.data
  let tmpData = {}
  const adobeAnalytic = data?.adobeAnalytic || null
  if (data.pageComponentProperties) {
    tmpData = data.pageComponentProperties
  } else {
    tmpData = data
  }
  tmpData = filterBackEndJson(tmpData)
  const headertmp = filterBackEndJson(header)
  const footertmp = filterBackEndJson(footer)
  const datalayerPageName = data?.datalayerPageName
  return {
    props: {
      ip,
      tmpData,
      headertmp,
      footertmp,
      adobeAnalytic,
      globalScript,
      footerScript,
      topHeadScript,
      seoPageScript,
      analyticsData,
      datalayerPageName,
      plpData,
      gbhDataLayerCustom,
      mappingData,
      ...(await serverSideTranslations(locale, ["plp"])),
    },
  }
}
export default Listing
